// mindmetal-app/web/src/components/mindmetal/ExportMenu.tsx
import { useState } from 'react'
import { Download, FileJson, FileText, Table } from 'lucide-react'
import { Button } from '@personic/shared'
import { exportProfile } from '@/hooks/useMindMetaLTestUsers'

interface ExportMenuProps {
  canonicalId: string
  className?: string
}

const FORMATS = [
  { value: 'yaml', label: 'YAML', icon: FileText, ext: 'yaml' },
  { value: 'json', label: 'JSON', icon: FileJson, ext: 'json' },
  { value: 'csv', label: 'CSV', icon: Table, ext: 'csv' },
] as const

export function ExportMenu({ canonicalId, className }: ExportMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState<string | null>(null)

  const handleExport = async (format: 'yaml' | 'json' | 'csv') => {
    setLoading(format)
    try {
      const data = await exportProfile(canonicalId, format)

      const blob = new Blob([data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${canonicalId}_profile.${format}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setLoading(null)
      setIsOpen(false)
    }
  }

  return (
    <div className={`relative ${className || ''}`}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        leftIcon={<Download className="w-4 h-4" />}
      >
        Export
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-warm-200 py-1 z-20">
            {FORMATS.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => handleExport(value)}
                disabled={loading !== null}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-midnight-700 hover:bg-warm-50 disabled:opacity-50"
              >
                <Icon className="w-4 h-4 text-warm-500" />
                {loading === value ? 'Exporting...' : label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
